import { z } from 'zod';

import {
  labelNarrowingOf,
  labelSearchField,
  type LabelNarrowing,
} from '@/features/labels';

/**
 * Board view modes. Compact phase view is the default; the expanded step view is
 * the two-level header the product's board is specified around.
 */
export const BOARD_VIEWS = ['phases', 'steps'] as const;
export type BoardView = (typeof BOARD_VIEWS)[number];

/**
 * Task detail tabs, in the order the product specification fixes them.
 *
 * Only the tabs the drawer can actually fill. The specification also names
 * Polls, Attachments, Artifacts, Files and Audit, and each will be added back
 * here by the feature that gives it an endpoint — the contract carries no poll,
 * attachment, artifact, workspace-file or audit data today, so a tab for one
 * could only describe what it will one day hold. Five such tabs beside four
 * working ones is what made the drawer read as unfinished.
 *
 * Removing a value is safe for anyone holding an old address: `boardSearchSchema`
 * catches an unrecognised `tab` and falls back to `details`. `inputs` was removed
 * that way — a task's declared input values are set once at creation and never
 * change, so a whole tab spent on them was a permanent cost for a question asked
 * about a task perhaps once. Links naming it still open the drawer on Details.
 */
export const TASK_TABS = [
  'details',
  'comments',
  'executions',
  'relations',
  'results',
] as const;
export type TaskTab = (typeof TASK_TABS)[number];

/**
 * The states a board can be narrowed to. These are the same three the header
 * summarises, because those counts are what a person is answering when they
 * reach for a filter: what is running, what is waiting on me, what broke.
 */
export const BOARD_STATE_FILTERS = ['running', 'waiting', 'failed'] as const;
export type BoardStateFilter = (typeof BOARD_STATE_FILTERS)[number];

export const DEFAULT_BOARD_VIEW: BoardView = 'phases';
export const DEFAULT_TASK_TAB: TaskTab = 'details';

/**
 * Search parameters for the board route.
 *
 * Search parameters are user-editable text, so an unrecognised value falls back
 * to the default rather than failing the route. Path parameters behave the other
 * way round: an unresolvable identifier is a not-found condition, because the
 * address names a resource that does not exist.
 *
 * `tab` is meaningless without `task` and is stripped when the drawer closes.
 *
 * Which workflow's board this is comes from the `$workflowKey` path parameter,
 * not from here: a board belongs to exactly one workflow, and an unresolvable
 * key is a not-found condition rather than something to fall back from.
 */
export const boardSearchSchema = z.object({
  view: z
    .enum(BOARD_VIEWS)
    .catch(DEFAULT_BOARD_VIEW)
    .default(DEFAULT_BOARD_VIEW),
  task: z.uuid().optional().catch(undefined),
  // Optional, and "absent" is a value the drawer reads (spec 095 FR-004,
  // FR-006): an address that names no tab may land on Results when the task
  // has produced something and nothing is running, while an address that
  // names Details means Details. Defaulting here would erase that difference.
  // `DEFAULT_TASK_TAB` is applied where the value is consumed, once the drawer
  // knows what the task has. An unrecognised or withdrawn value is "none".
  tab: z.enum(TASK_TABS).optional().catch(undefined),
  // Which workspace file is shown beside the open task. Meaningless without
  // `task` and stripped when the drawer closes, exactly as `tab` is.
  preview: z.string().optional().catch(undefined),
  // Whether the workflow's diagram is shown beside the board (spec 094 FR-012).
  //
  // In the address rather than in the preferences store because a reader shares
  // the view they are looking at — "here is the board with the diagram showing"
  // is a thing someone pastes into a chat, which is the constitution's own test
  // for what belongs here (§4).
  //
  // Defaults to false: the board is what the reader came for, and a diagram that
  // appeared uninvited would spend half the width on the question they did not
  // ask (FR-011). `catch` rather than a strict parse, like every parameter here —
  // a hand-edited `?diagram=yes` falls back to hidden instead of failing the
  // route.
  // Parsed, not coerced. `z.coerce.boolean()` is JavaScript truthiness: every
  // non-empty string becomes `true`, so `?diagram=banana` would show the diagram
  // and — worse — `?diagram=false` would too. Only the literal `true` means true;
  // anything else falls back to hidden, which is the default a reader who typed
  // nonsense would expect.
  //
  // `preprocess` rather than a union of literals: a union constrains the schema's
  // *input* type to exactly those literals, and every existing
  // `navigate({ search: (prev) => ({ ...prev }) })` on this route then fails to
  // type-check, because `prev.diagram` is the parsed `boolean` and not one of
  // them. Preprocessing takes `unknown` in and a `boolean` out, so reading the
  // parsed value back into a navigation is the ordinary thing it looks like.
  diagram: z
    .preprocess((value) => value === true || value === 'true', z.boolean())
    .catch(false)
    .default(false),
  // Asks the board to open its New task dialog on arrival. This is how the
  // command palette runs the action from anywhere (spec 077 FR-015): the
  // dialog has one owner, the board, and the palette navigates to it rather
  // than opening a copy. It means "the dialog is open", as `task` means "the
  // drawer is open", and leaves the address when the dialog is dismissed. Same
  // literal-`true` parsing as `diagram`, for the same reasons.
  create: z
    .preprocess((value) => value === true || value === 'true', z.boolean())
    .catch(false)
    .default(false),
  // Absent means "everything", which is why this has no default: a board is
  // unfiltered until someone says otherwise, and the address should say so by
  // carrying nothing rather than by carrying the word "all".
  state: z.enum(BOARD_STATE_FILTERS).optional().catch(undefined),
  // Free text, matched against a task's title and description. Same parameter
  // name and same meaning as the workspace task list's, so a `?q=` copied from
  // one address to the other narrows to the same thing.
  q: z.string().optional().catch(undefined),
  // Label identifiers joined by commas, or the reserved word `none`. Kept as
  // the raw string here and interpreted by `boardLabelNarrowing`: the schema's
  // job is to get user-editable text through without failing the route, and it
  // has no registry to check an identifier against anyway. A label named here
  // that no longer exists produces an empty board, which is the honest answer —
  // not a not-found.
  labels: labelSearchField,
});

export type BoardSearch = z.infer<typeof boardSearchSchema>;

/**
 * The narrowing a parsed board address asks for.
 *
 * Delegates to `features/labels` rather than reading the string itself, so the
 * encoding lives in exactly one place: the Overview route asks the same
 * question of its own search parameters, and two readers of one format is how
 * a shared link starts meaning different things on different pages.
 */
export function boardLabelNarrowing(search: BoardSearch): LabelNarrowing {
  return labelNarrowingOf(search);
}

/** Human-readable tab labels. Kept beside the keys so they cannot drift apart. */
export const TASK_TAB_LABELS: Record<TaskTab, string> = {
  details: 'Details',
  comments: 'Comments',
  // "Runs": the word every other surface uses (spec 128 FR-017); the key
  // stays the contract's `executions`.
  executions: 'Runs',
  relations: 'Relations',
  results: 'Results',
};

export const BOARD_VIEW_LABELS: Record<BoardView, string> = {
  phases: 'Phases',
  steps: 'Steps',
};
